import { captureException } from '@sentry/react';
import * as React from 'react';
import { ErrorBoundary as ReactErrorBoundary } from 'react-error-boundary';

import GeneralErrorMessage from '../../common/notifications/general-error-message';

type DebugComponentProps = {
  error?: Error;
  info?: React.ErrorInfo;
};

const DebugComponent = ({ error, info }: DebugComponentProps) => (
  <div style={{ padding: 10, overflow: 'scroll', backgroundColor: '#fee', color: '#e00' }}>
    <h3>{error?.toString()}</h3>
    <p>This is located at:</p>
    <pre>{info?.componentStack}</pre>
  </div>
);

type ErrorBoundaryProps = {
  // We use the fallback component only in production (SentryErrorBoundary).
  FallbackComponent?: React.Factory<DebugComponentProps>;
  children?: React.ReactNode | React.ReactNode[];
};

type ErrorBoundaryState = {
  hasError: boolean;
  error?: Error;
  info?: React.ErrorInfo;
};

const SentryErrorBoundary = ({ children }: ErrorBoundaryProps) => {
  return (
    <ReactErrorBoundary
      fallbackRender={() => <GeneralErrorMessage />}
      onError={(error: Error, info: React.ErrorInfo) =>
        captureException(error, { extra: { componentStack: info?.componentStack } })
      }>
      {children}
    </ReactErrorBoundary>
  );
};

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  public constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    // Update state so the next render will show the fallback UI.
    return { hasError: true };
  }
  public componentDidCatch(error: Error, info?: React.ErrorInfo) {
    this.setState({ hasError: true, error, info });
  }
  public render() {
    return this.state.hasError ? (
      <DebugComponent error={this.state.error} info={this.state.info} />
    ) : (
      this.props.children
    );
  }
}

export default __IS_DEVELOPMENT__ ? ErrorBoundary : SentryErrorBoundary;
